import React, { useState } from 'react';
import { X, Upload, CheckCircle, XCircle, Loader2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUpload } from '../context/UploadContext';

const UploadTracker = ({ isOpen, onClose }) => {
  const { uploadJobs, removeUploadJob, clearCompleted, getCounts } = useUpload();
  const [expandedJobs, setExpandedJobs] = useState({});

  const toggleJobExpand = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Upload className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const counts = getCounts();

  if (!isOpen) return null;

  return (
    <div className="fixed top-16 right-4 w-96 max-h-[600px] bg-white rounded-lg shadow-2xl border border-gray-200 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Upload Manager</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {counts.uploading > 0 && `${counts.uploading} uploading`}
            {counts.uploading > 0 && counts.completed > 0 && ' · '}
            {counts.completed > 0 && `${counts.completed} completed`}
            {counts.failed > 0 && ` · ${counts.failed} failed`}
            {counts.total === 0 && 'No uploads'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {counts.completed > 0 && (
            <button
              onClick={clearCompleted}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Clear completed"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Upload Jobs List */}
      <div className="flex-1 overflow-y-auto">
        {uploadJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Upload className="w-12 h-12 mb-2" />
            <p className="text-sm">No uploads</p>
          </div>
        ) : (
          <AnimatePresence>
            {uploadJobs.map((job) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 100 }}
                className="border-b border-gray-100 last:border-b-0"
              >
                {/* Job Summary */}
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    {/* Status Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(job.status)}
                    </div>

                    {/* Job Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {job.fileName}
                        </h4>
                        <button
                          onClick={() => removeUploadJob(job.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span>
                          {job.fileCount > 0 && `${job.fileCount} file(s)`}
                          {job.fileCount > 0 && job.folderCount > 0 && ' · '}
                          {job.folderCount > 0 && `${job.folderCount} folder(s)`}
                        </span>
                        <span>{formatTime(job.startTime)}</span>
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status === 'uploading' && 'Uploading'}
                          {job.status === 'processing' && 'Processing'}
                          {job.status === 'completed' && 'Completed'}
                          {job.status === 'failed' && 'Failed'}
                        </span>

                        {(job.status === 'uploading' || job.status === 'processing') && (
                          <span className="text-xs text-gray-600 font-medium">
                            {job.progress}%
                          </span>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {(job.status === 'uploading' || job.status === 'processing') && (
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 transition-all duration-500"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                      )}

                      {/* Expand/Collapse Button */}
                      {(job.processed > 0 || job.failed > 0) && (
                        <button
                          onClick={() => toggleJobExpand(job.id)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2"
                        >
                          {expandedJobs[job.id] ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Hide details
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              Show details
                            </>
                          )}
                        </button>
                      )}

                      {/* Expanded Details */}
                      <AnimatePresence>
                        {expandedJobs[job.id] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mt-2 pt-2 border-t border-gray-200"
                          >
                            <div className="space-y-1 text-xs">
                              {job.processed > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Processed:</span>
                                  <span className="font-medium text-gray-900">
                                    {job.processed} of {job.total}
                                  </span>
                                </div>
                              )}
                              {job.failed > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Failed:</span>
                                  <span className="font-medium text-red-600">
                                    {job.failed}
                                  </span>
                                </div>
                              )}
                              {job.error && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                                  {job.error}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default UploadTracker;
